import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import stickybits from 'stickybits';

// Components.
import FormSubmitResponse from './formSubmitResponse';

// Config.
import { FORM_VALUES_SHAPE } from '../config/proptypes';

// Services.
import emailRequired from '../services/emailRequired';
import phoneRequired from '../services/phoneRequired';

// Styles.
import '../scss/formSubmit.scss';

const FormSubmit = ({
  errors,
  formValues: {
    consent,
    email,
    first_name: firstName,
    last_name: lastName,
    zip_code: zipCode,
    phone,
  },
  loading,
  onFormSubmit,
  onInputChange,
  selectedLists,
  status,
}) => {
  // Grab reCAPTCHA from global window.
  const grecaptcha = window.grecaptcha || '';
  const recaptchaPublicKey = window.kauffmanReCAPTCHA || '';

  // Initialize the sticky bits.
  useEffect(() => {
    const formSubmitEl = document
      .querySelector('.newsletters__form-submit');
    if (formSubmitEl) {
      stickybits(formSubmitEl, {
        noStyles: true,
        useGetBoundingClientRect: true,
        useStickyClasses: true,
        verticalPosition: 'bottom',
      });
    }
  }, []);

  // Fork for styles based on whether lists were selected.
  const styles = {
    bottom: ! selectedLists.length
      ? 'auto'
      : 0,
    opacity: ! selectedLists.length
      ? 0
      : 1,
    pointerEvents: ! selectedLists.length
      ? 'none'
      : 'auto',
    transform: ! selectedLists.length
      ? 'translateY(10px)'
      : 'translateY(0)',
  };

  // Handle success messaging.
  if ('subscribed' === status && 0 === errors.length) {
    return (
      <div
        className="newsletters__form-submit"
        style={styles}
      >
        <div className="newsletters__form-submit-inner">
          <FormSubmitResponse
            errors={errors}
            status={status}
          />
        </div>
      </div>
    );
  }

  // Helper function for handling straightforward input changes.
  const onChange = (event) => onInputChange(
    event.target.name,
    event.target.value.trim()
  );

  return (
    <div
      className="newsletters__form-submit"
      style={styles}
    >
      <div className="newsletters__form-submit-inner">
        <h3>
          Select newsletters and enter your info
        </h3>
        <div className="newsletters__form-input-row">
          <label htmlFor="first_name">
            <span className="screen-reader-text">
              First Name
            </span>
            <input
              disabled={'none' === styles.pointerEvents} // Should not be focusable when form is hidden.
              id="first_name"
              name="first_name"
              onChange={onChange}
              placeholder="First Name"
              required
              type="text"
              value={firstName}
            />
          </label>
          <label htmlFor="last_name">
            <span className="screen-reader-text">
              Last Name
            </span>
            <input
              disabled={'none' === styles.pointerEvents}
              id="last_name"
              name="last_name"
              onChange={onChange}
              placeholder="Last Name"
              required
              type="text"
              value={lastName}
            />
          </label>
          <label htmlFor="zip_code">
            <span className="screen-reader-text">
              ZIP Code
            </span>
            <input
              disabled={'none' === styles.pointerEvents}
              id="zip_code"
              name="zip_code"
              onChange={onChange}
              placeholder="ZIP Code"
              required
              type="text"
              value={zipCode}
            />
          </label>
          {emailRequired(selectedLists) ? (
            <label htmlFor="email">
              <span className="screen-reader-text">
                Email
              </span>
              <input
                disabled={'none' === styles.pointerEvents}
                id="email"
                name="email"
                onChange={onChange}
                placeholder="Email"
                required
                type="email"
                value={email}
              />
            </label>
          ) : null}
          {phoneRequired(selectedLists) ? (
            <label htmlFor="phone">
              <span className="screen-reader-text">
                Phone Number
              </span>
              <input
                disabled={'none' === styles.pointerEvents}
                id="phone"
                name="phone"
                onChange={onChange}
                placeholder="Phone number"
                required
                type="phone"
                value={phone}
              />
            </label>
          ) : null}
          <input
            aria-busy={loading}
            disabled={'none' === styles.pointerEvents}
            onClick={(event) => {
              event.preventDefault();
              if (grecaptcha && recaptchaPublicKey) {
                grecaptcha
                  .execute(recaptchaPublicKey.publicKey, { action: 'form' })
                  .then((token) => onFormSubmit(token));
              }
            }}
            type="submit"
            value={loading ? 'Please Wait...' : 'Sign Up'}
          />
        </div>
        <FormSubmitResponse
          errors={errors}
          status={status}
        />
        <label
          className="consent"
          htmlFor="newsletter-consent"
        >
          <input
            checked={consent}
            disabled={'none' === styles.pointerEvents}
            id="newsletter-consent"
            name="consent"
            onChange={() => onInputChange('consent', ! consent)}
            required
            type="checkbox"
            value="1"
          />
          <p>
            I agree that the Foundation may use my personal information to
            communicate with me about the Foundation and programs I may find
            interesting, to personalize my experience on Foundation web sites,
            conduct academic/charitable research, and share my data with
            trusted Foundation affiliates. Please review
            our
            {' '}
            <a
              tabIndex={'none' === styles.pointerEvents ? '-1' : '0'}
              href="/privacy-policy"
            >
              privacy policy
            </a>
            {' '}
            which provides info on how we use and process your data.
          </p>
        </label>
      </div>
    </div>
  );
};

FormSubmit.propTypes = {
  errors: PropTypes.arrayOf(PropTypes.string).isRequired,
  formValues: PropTypes.shape(FORM_VALUES_SHAPE).isRequired,
  loading: PropTypes.bool.isRequired,
  onFormSubmit: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
  selectedLists: PropTypes.arrayOf(PropTypes.string).isRequired,
  status: PropTypes.string.isRequired,
};

export default FormSubmit;
